// Modal para gestionar la carga masiva de productos desde un archivo Excel
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { Button } from '@shared/components/ui/button';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpFromBracket, faInfoCircle, faFileArrowDown } from '@fortawesome/pro-light-svg-icons';

interface BulkUploadModalProps {
  onDownloadTemplate: () => void;
  onUpload: (file: File) => Promise<{
    hasErrors?: boolean;
    errorCount?: number;
    successCount?: number;
    errorFileUrl?: string;
    message?: string;
  } | void>;
  isUploading?: boolean;
}

export interface BulkUploadModalRef {
  open: () => void;
  close: () => void;
}

export const BulkUploadModal = forwardRef<BulkUploadModalRef, BulkUploadModalProps>(
  ({ onDownloadTemplate, onUpload, isUploading = false }, ref) => {
    const { t } = useTranslation('productsModule');
    const modalRef = useRef<ModalCenterRef>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [dragActive, setDragActive] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [uploadErrors, setUploadErrors] = useState<{
      errorCount: number;
      successCount: number;
      errorFileUrl: string;
    } | null>(null);

    useImperativeHandle(
      ref,
      () => ({
        open: () => {
          resetState();
          modalRef.current?.openModal();
        },
        close: () => {
          modalRef.current?.closeModal();
        },
      }),
      [],
    );

    const resetState = () => {
      setSelectedFile(null);
      setDragActive(false);
      setError(null);
      setUploadErrors(null);
      if (inputRef.current) {
        inputRef.current.value = '';
      }
    };

    const handleClose = () => {
      if (!isUploading) {
        modalRef.current?.closeModal();
      }
    };

    const handleFileSelect = (file?: File) => {
      if (!file) {
        return;
      }
      if (!file.name.toLowerCase().endsWith('.xlsx')) {
        setError(t('modal.bulk_upload.invalid_file'));
        return;
      }
      setSelectedFile(file);
      setError(null);
    };

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      if (isUploading) {
        return;
      }
      const file = event.target.files?.[0];
      handleFileSelect(file);
    };

    const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();
      if (isUploading) {
        return;
      }
      setDragActive(false);
      const file = event.dataTransfer.files?.[0];
      handleFileSelect(file);
    };

    const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();
      if (isUploading) {
        return;
      }
      setDragActive(true);
    };

    const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();
      if (isUploading) {
        return;
      }
      setDragActive(false);
    };

    const handleUpload = async () => {
      if (!selectedFile) {
        setError(t('modal.bulk_upload.no_file'));
        return;
      }
      setError(null);
      setUploadErrors(null);

      const result = await onUpload(selectedFile);

      // Si hay errores en la respuesta, mostrar información de errores
      if (result?.hasErrors && result.errorCount && result.errorFileUrl) {
        setUploadErrors({
          errorCount: result.errorCount,
          successCount: result.successCount || 0,
          errorFileUrl: result.errorFileUrl,
        });
      }
    };

    const handleClickDropzone = () => {
      if (isUploading) {
        return;
      }
      inputRef.current?.click();
    };

    const renderDropzoneContent = () => {
      if (selectedFile) {
        return (
          <div className="text-center space-y-1">
            <FontAwesomeIcon icon={faArrowUpFromBracket} className="!h-10 !w-10 text-primary" />
            <p className="text-sm text-title font-medium pt-4">
              {t('modal.bulk_upload.selected_file_label')}{' '}
              <span className="text-complementary-text">{selectedFile.name}</span>
            </p>
          </div>
        );
      }

      return (
        <div className="text-center space-y-1">
          <FontAwesomeIcon icon={faArrowUpFromBracket} className="h-9 w-9 text-icons" />
          <p className="hidden text-sm text-complementary-text pt-4 sm:block">
            {t('modal.bulk_upload.dropzone_prompt')}
          </p>
          <p className="text-sm text-complementary-text pt-4 sm:hidden">
            {t('modal.bulk_upload.dropzone_prompt_mobile')}
          </p>
          <p className="text-sm text-complementary-text font-semibold">{t('modal.bulk_upload.format_hint')}</p>
        </div>
      );
    };

    const dropzoneClasses = [
      'flex',
      'flex-col',
      'items-center',
      'justify-center',
      'gap-2',
      'px-6',
      'py-6',
      'rounded-lg',
      'border',
      'border-dashed',
      dragActive ? 'border-primary bg-card-bg' : 'border-title bg-background',
      isUploading ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
      'transition-colors',
    ].join(' ');

    const modalTitle = <span className="block text-left sm:text-center">{t('modal.bulk_upload.title')}</span>;

    return (
      <ModalCenter ref={modalRef} title={modalTitle} width="lg" type="default">
        <div className="space-y-6">
          <p className="text-sm">{t('modal.bulk_upload.description')}</p>

          <div className="flex flex-col gap-3 rounded-lg border border-card-border bg-card-bg p-4">
            <div className="flex flex-col gap-2 text-center sm:flex-row sm:items-center sm:justify-between sm:text-left sm:gap-4">
              <span className="text-sm text-title flex items-center justify-center gap-2 text-center sm:justify-start sm:text-left">
                <FontAwesomeIcon icon={faInfoCircle} className="h-4 w-4 text-icons" />
                {t('modal.bulk_upload.download_hint')}
              </span>
              <Button
                variant="outline"
                size="sm"
                className="w-fit self-center sm:self-auto"
                onClick={onDownloadTemplate}
                data-testid="bulk-upload-download-button"
              >
                {t('modal.bulk_upload.buttons.download')}
              </Button>
            </div>
          </div>

          <div
            className={dropzoneClasses}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={handleClickDropzone}
            data-testid="bulk-upload-dropzone"
          >
            {renderDropzoneContent()}
            <input
              ref={inputRef}
              type="file"
              accept=".xlsx"
              className="hidden"
              onChange={handleInputChange}
              data-testid="bulk-upload-input"
            />
          </div>

          {error && (
            <p className="text-sm text-error" data-testid="bulk-upload-error">
              {error}
            </p>
          )}

          {/* Mensaje de errores en la importación con enlace de descarga */}
          {uploadErrors && (
            <div className="rounded-lg border border-error p-4 space-y-3" data-testid="bulk-upload-errors-message">
              <div className="flex items-start gap-2">
                <FontAwesomeIcon icon={faInfoCircle} className="h-5 w-5 text-error mt-0.5 flex-shrink-0" />
                <div className="flex-1 space-y-2">
                  <p className="text-sm font-medium text-error">
                    {t('modal.bulk_upload.errors_detected', {
                      errorCount: uploadErrors.errorCount,
                      successCount: uploadErrors.successCount,
                    })}
                  </p>
                  <p className="text-sm text-complementary-text">{t('modal.bulk_upload.errors_description')}</p>
                  <Button
                    variant="outline"
                    size="lg"
                    onClick={() => window.open(uploadErrors.errorFileUrl, '_blank', 'noopener')}
                    className="w-fit"
                    icon={faFileArrowDown}
                    iconPosition="start"
                    data-testid="bulk-upload-download-errors-link"
                  >
                    {t('modal.bulk_upload.download_errors_file')}
                  </Button>
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              size="lg"
              onClick={handleClose}
              disabled={isUploading}
              data-testid="bulk-upload-cancel-button"
            >
              {t('buttons.cancel')}
            </Button>
            <Button
              variant="primary"
              size="lg"
              onClick={handleUpload}
              loading={isUploading}
              disabled={!selectedFile}
              data-testid="bulk-upload-submit-button"
            >
              {isUploading ? t('modal.bulk_upload.buttons.uploading') : t('modal.bulk_upload.buttons.upload')}
            </Button>
          </div>
        </div>
      </ModalCenter>
    );
  },
);

BulkUploadModal.displayName = 'BulkUploadModal';
